import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2, Wand2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

// Lands here when the user taps the magic-link CTA in their inbox. We
// auto-redeem the URL token, set the session, and redirect to the
// dashboard. Failure modes: expired link, already-used link, server
// outage — all surface a clear error + a "request a new link" CTA so
// the user never feels stuck.
const MagicLoginPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { loginWithMagicToken, user } = useAuth();
  const [status, setStatus] = useState("verifying"); // 'verifying' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState("");
  // StrictMode double-invokes useEffect in development. Without this sentinel
  // we'd POST /redeem twice — the first call consumes the token, the second
  // returns 400 ("already used") and overwrites the success state. The ref
  // survives the dev-only re-mount and guarantees we only attempt redeem
  // exactly once per browser session for this URL.
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (attemptedRef.current) return undefined;
    attemptedRef.current = true;

    // If the user is somehow already authed in this tab (e.g. they tapped
    // the link from a device that's still signed in), skip the dance and
    // bounce straight to the dashboard.
    if (user) {
      setStatus("success");
      const t = setTimeout(() => navigate("/dashboard", { replace: true }), 600);
      return () => clearTimeout(t);
    }
    if (!token) {
      setStatus("error");
      setErrorMsg("This sign-in link is missing its token.");
      return undefined;
    }
    // Intentionally no `cancelled` flag here: React 18's StrictMode preserves
    // refs across the dev double-mount, so `attemptedRef` already guarantees
    // we only fire the redeem ONCE per page session. A `cancelled` flag
    // would set true during the simulated unmount and then skip the
    // setStatus("success") that happens after the await resolves —
    // leaving the user stuck on "Signing you in…" forever.
    (async () => {
      try {
        await loginWithMagicToken(token);
        setStatus("success");
        toast.success("Signed in. Welcome back!");
        setTimeout(() => navigate("/dashboard", { replace: true }), 800);
      } catch (err) {
        setStatus("error");
        setErrorMsg(err.response?.data?.detail || "This sign-in link is invalid or has already been used.");
      }
    })();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black flex items-center justify-center px-4 py-12" data-testid="magic-login-page">
      <div className="w-full max-w-md bg-gray-900/80 backdrop-blur border border-gray-800 rounded-2xl p-8 text-center">
        {status === "verifying" && (
          <div data-testid="magic-login-verifying">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                <Loader2 className="h-7 w-7 text-emerald-400 animate-spin" />
              </div>
            </div>
            <h1 className="text-white text-xl font-semibold mb-2">Signing you in…</h1>
            <p className="text-gray-400 text-sm">Just a moment while we verify your link.</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="magic-login-success">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                <CheckCircle2 className="h-7 w-7 text-emerald-400" />
              </div>
            </div>
            <h1 className="text-white text-xl font-semibold mb-2">You're in!</h1>
            <p className="text-gray-400 text-sm">Redirecting you to your dashboard…</p>
          </div>
        )}

        {status === "error" && (
          <div data-testid="magic-login-error">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center">
                <AlertCircle className="h-7 w-7 text-red-400" />
              </div>
            </div>
            <h1 className="text-white text-xl font-semibold mb-2">Link didn't work</h1>
            <p className="text-gray-400 text-sm mb-6">{errorMsg}</p>
            <div className="space-y-2">
              <Link to="/" className="block">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="magic-login-request-new-btn">
                  <Wand2 className="h-4 w-4 mr-2" /> Request a new sign-in link
                </Button>
              </Link>
              <Link to="/forgot-password" className="block">
                <Button variant="outline" className="w-full border-gray-700 text-gray-300 hover:bg-gray-800">
                  Reset password instead
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MagicLoginPage;
